import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { ApiError } from '../../api/client'
import { officesQuery } from '../../api/settingsAuthentication'
import {
  createScimToken,
  isScimPage,
  revokeScimToken,
  saveScimSettings,
  scimQuery,
  type ScimPage,
} from '../../api/settingsScim'
import { toast } from '../../ui/notify'

/**
 * Settings -> Authentication -> "Provisioning (SCIM)": IAM-ul clientului
 * impinge oamenii la noi, intrarea ramane prin SSO.
 *
 * Panoul isi cere singur datele (`/settings/authentication/scim`). Un raspuns
 * care nu e al lui (server mai vechi, 403) inseamna ca panoul nu se arata -
 * restul ecranului ramane exact cum era.
 *
 * JETONUL SE ARATA O SINGURA DATA, imediat dupa generare, intr-o caseta de
 * copiat. Lista arata numai inceputul lui. Un jeton pierdut nu se recupereaza:
 * se revoca si se face altul.
 */

type Form = {
  enabled: boolean
  provider_id: number
  link_at_login: boolean
  company_id: number
  office_id: number
  unmapped_groups: string
  ip_allowlist: string
  mtls_trust: boolean
  mtls_header: string
  mtls_fingerprints: string
  disable_jit: boolean
}

function formOf(page: ScimPage): Form {
  const s = page.settings
  return {
    enabled: s.enabled,
    provider_id: s.providerId,
    link_at_login: s.linkAtLogin,
    company_id: s.companyId,
    office_id: s.officeId,
    unmapped_groups: s.unmappedGroups,
    ip_allowlist: s.ipAllowlist,
    mtls_trust: s.mtlsTrust,
    mtls_header: s.mtlsHeader,
    mtls_fingerprints: s.mtlsFingerprints,
    disable_jit: s.disableJit,
  }
}

/** Culoarea unui cod de raspuns in jurnal. */
export function statusKind(status: number): string {
  if (status >= 500) return 'danger'
  if (status >= 400) return 'warning'
  return 'success'
}

function copy(value: string, what: string) {
  void navigator.clipboard?.writeText(value)
  toast(`${what} copied.`, 'Copied', 'success')
}

export function ScimProvisioningPanel() {
  const client = useQueryClient()
  const query = useQuery(scimQuery)
  const page = isScimPage(query.data) ? query.data : null
  const [form, setForm] = useState<Form | null>(null)
  const [busy, setBusy] = useState(false)
  const [tokenName, setTokenName] = useState('')
  const [tokenExpiry, setTokenExpiry] = useState('')
  const [fresh, setFresh] = useState<string | null>(null)

  /* Formularul se reia din ce a raspuns serverul, la fiecare raspuns nou. */
  useEffect(() => {
    if (isScimPage(query.data)) setForm(formOf(query.data))
  }, [query.data])

  const offices = useQuery({ ...officesQuery(form?.company_id ?? 0), enabled: (form?.company_id ?? 0) > 0 })

  if (!page || !form) return null

  const refresh = () => {
    void client.invalidateQueries({ queryKey: ['settings', 'authentication'] })
  }
  const set = <K extends keyof Form>(key: K, value: Form[K]) => setForm({ ...form, [key]: value })
  const disabled = !page.canWrite
  const linked = page.providers.find((p) => p.id === form.provider_id)
  const companyName = (id: number) => page.companies.find((c) => c.id === id)?.name ?? `#${id}`

  const save = async () => {
    setBusy(true)
    try {
      const answer = await saveScimSettings(form)
      toast(answer.message, answer.status === 'OK' ? 'Saved' : 'Error', answer.status === 'OK' ? 'success' : 'error')
      if (answer.status === 'OK') refresh()
    } catch (error) {
      toast(error instanceof ApiError ? error.message : 'The settings could not be saved.', 'Error', 'error')
    } finally {
      setBusy(false)
    }
  }

  const generate = async () => {
    setBusy(true)
    try {
      const answer = await createScimToken(tokenName.trim(), tokenExpiry)
      if (answer.status === 'OK' && answer.token) {
        setFresh(answer.token)
        setTokenName('')
        setTokenExpiry('')
        refresh()
      } else {
        toast(answer.message, 'Error', 'error')
      }
    } catch (error) {
      toast(error instanceof ApiError ? error.message : 'The token could not be created.', 'Error', 'error')
    } finally {
      setBusy(false)
    }
  }

  const revoke = async (id: number, name: string) => {
    if (!window.confirm(`Revoke the token "${name}"? The IAM system using it will be refused from now on.`)) return
    const answer = await revokeScimToken(id)
    toast(answer.message, answer.status === 'OK' ? 'Revoked' : 'Error', answer.status === 'OK' ? 'success' : 'error')
    if (answer.status === 'OK') refresh()
  }

  /* Un server mai vechi nu trimite `restUrl`: se scoate din adresa SCIM, construita la fel. */
  const restUrl = page.restUrl ?? page.baseUrl.replace(/\/api\/v2\/scim\/v2$/, '/api/v2/iam/v1')

  return (
    <div className="portlet light bordered" id="scimProvisioning">
      <div className="portlet-body">
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
          <h4 style={{ margin: 0, flex: 1 }}>Provisioning (SCIM 2.0 or REST API)</h4>
          <span className={`label label-${page.settings.enabled ? 'success' : 'default'}`} style={{ fontWeight: 'normal' }}>
            {page.settings.enabled ? 'on' : 'off'}
          </span>
        </div>
        <p className="text-muted" style={{ marginTop: 0 }}>
          SCIM 2.0 or REST API - the identity management system uses one of them, with the same token. It creates, changes
          and deactivates accounts here. People still sign in only with single sign-on.
        </p>
        <div className="text-muted" style={{ fontSize: 12, marginBottom: 12 }} data-testid="scimStatus">
          Last successful call: {page.status.lastSuccess || 'never'} · Errors in the last 24 hours:{' '}
          <span className={page.status.errors24h > 0 ? 'text-danger' : undefined}>{page.status.errors24h}</span> · Calls in
          the last 24 hours: {page.status.calls24h}
        </div>

        <div className="checkbox" style={{ marginTop: 0 }}>
          <label>
            <input
              type="checkbox"
              checked={form.enabled}
              disabled={disabled}
              onChange={(e) => set('enabled', e.target.checked)}
            />{' '}
            Accept SCIM provisioning requests
          </label>
        </div>

        <label className="control-label">SCIM base URL</label>
        <div className="input-group" style={{ maxWidth: 640 }}>
          <input type="text" className="form-control" value={page.baseUrl} readOnly aria-label="SCIM base URL" />
          <span className="input-group-btn">
            <button className="btn btn-default" type="button" onClick={() => copy(page.baseUrl, 'Address')}>
              Copy
            </button>
          </span>
        </div>
        <span className="help-block">Give this address and a token to the identity management system (Tenant URL).</span>

        <label className="control-label">REST API base URL</label>
        <div className="input-group" style={{ maxWidth: 640 }}>
          <input type="text" className="form-control" value={restUrl} readOnly aria-label="REST API base URL" />
          <span className="input-group-btn">
            <button className="btn btn-default" type="button" onClick={() => copy(restUrl, 'Address')}>
              Copy
            </button>
          </span>
        </div>
        <span className="help-block">
          The same provisioning as a plain REST API, for systems without SCIM. Same token, same address list and mTLS,
          same call log.
        </span>

        <div className="row">
          <div className="col-md-6">
            <div className="form-group">
              <label className="control-label" htmlFor="scimProvider">
                Single sign-on provider
              </label>
              <select
                id="scimProvider"
                className="form-control"
                value={form.provider_id}
                disabled={disabled}
                onChange={(e) => set('provider_id', Number(e.target.value))}
              >
                <option value={0}>Choose…</option>
                {page.providers.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name} ({p.protocol === 'saml' ? 'SAML' : 'OpenID Connect'})
                  </option>
                ))}
              </select>
              <span className="help-block">
                Provisioned accounts sign in with this provider. The SCIM <code>userName</code> must equal the name the
                provider sends at sign-in: for SAML the NameID (in Entra ID, e.g. the claim{' '}
                <code>user.onpremisessamaccountname</code>, which gives <code>ipopescu</code>); for OpenID Connect{' '}
                <code>onprem_sam_account_name</code>, <code>preferred_username</code> or <code>upn</code>.
              </span>
            </div>
            <div className="checkbox">
              <label>
                <input
                  type="checkbox"
                  checked={form.link_at_login}
                  disabled={disabled}
                  onChange={(e) => set('link_at_login', e.target.checked)}
                />{' '}
                Link the provisioned account at its first sign-in (by user name)
              </label>
            </div>
            <div className="checkbox">
              <label>
                <input
                  type="checkbox"
                  checked={form.disable_jit}
                  disabled={disabled || !linked}
                  onChange={(e) => set('disable_jit', e.target.checked)}
                />{' '}
                Do not create accounts at first sign-in (only SCIM creates them)
              </label>
              <span className="help-block" style={{ marginBottom: 0 }}>
                Sets &quot;New accounts&quot; of the provider to &quot;Refuse&quot;; unticking sets it back to &quot;Create
                the account&quot;.
              </span>
            </div>
          </div>
          <div className="col-md-6">
            <div className="form-group">
              <label className="control-label" htmlFor="scimCompany">
                Company for new accounts
              </label>
              <select
                id="scimCompany"
                className="form-control"
                value={form.company_id}
                disabled={disabled}
                onChange={(e) => setForm({ ...form, company_id: Number(e.target.value), office_id: 0 })}
              >
                <option value={0}>The company of the provider</option>
                {page.companies.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>
            {form.company_id > 0 ? (
              <div className="form-group">
                <label className="control-label" htmlFor="scimOffice">
                  Office
                </label>
                <select
                  id="scimOffice"
                  className="form-control"
                  value={form.office_id}
                  disabled={disabled}
                  onChange={(e) => set('office_id', Number(e.target.value))}
                >
                  <option value={0}>Choose…</option>
                  {(offices.data?.offices ?? []).map((o) => (
                    <option key={o.id} value={o.id}>
                      {o.name}
                    </option>
                  ))}
                </select>
              </div>
            ) : null}
            {/* Fara alegere intre "suspend" si "block" (16 octombrie 2026): oprirea din
                IAM e Block-ul din lista de conturi, si nimic altceva. */}
            <p className="help-block" data-testid="scim-deactivation">
              When the IAM deactivates someone, the account is blocked, exactly as with Block in the user list. Accounts
              are never deleted; when the IAM activates them again they are unblocked.
            </p>
            <div className="form-group">
              <label className="control-label" htmlFor="scimUnmapped">
                A user in no mapped group
              </label>
              <select
                id="scimUnmapped"
                className="form-control"
                value={form.unmapped_groups}
                disabled={disabled}
                onChange={(e) => set('unmapped_groups', e.target.value)}
              >
                <option value="ignore">Accept, with the default role of the provider</option>
                <option value="reject">Reject the request</option>
              </select>
            </div>
          </div>
        </div>

        <div className="row">
          <div className="col-md-6">
            <div className="form-group">
              <label className="control-label" htmlFor="scimIps">
                Allowed addresses
              </label>
              <textarea
                id="scimIps"
                className="form-control"
                rows={3}
                value={form.ip_allowlist}
                disabled={disabled}
                placeholder={'10.20.30.40\n10.20.0.0/16'}
                onChange={(e) => set('ip_allowlist', e.target.value)}
              />
              <span className="help-block">One address or network per line. Empty: every request is refused.</span>
            </div>
          </div>
          <div className="col-md-6">
            <div className="checkbox" style={{ marginTop: 24 }}>
              <label>
                <input
                  type="checkbox"
                  checked={form.mtls_trust}
                  disabled={disabled}
                  onChange={(e) => set('mtls_trust', e.target.checked)}
                />{' '}
                Also require a client certificate (mTLS)
              </label>
            </div>
            {form.mtls_trust ? (
              <>
                <div className="form-group">
                  <label className="control-label" htmlFor="scimHeader">
                    Header set by the web server
                  </label>
                  <input
                    id="scimHeader"
                    className="form-control"
                    value={form.mtls_header}
                    disabled={disabled}
                    onChange={(e) => set('mtls_header', e.target.value)}
                  />
                </div>
                <div className="form-group">
                  <label className="control-label" htmlFor="scimPrints">
                    Certificate fingerprints
                  </label>
                  <textarea
                    id="scimPrints"
                    className="form-control"
                    rows={2}
                    value={form.mtls_fingerprints}
                    disabled={disabled}
                    onChange={(e) => set('mtls_fingerprints', e.target.value)}
                  />
                  <span className="help-block">
                    SHA-256 (or SHA-1), one per line. Turn this on only when the web server verifies the certificate and
                    sets the header itself; otherwise anyone could send the header.
                  </span>
                </div>
              </>
            ) : null}
          </div>
        </div>

        {page.canWrite ? (
          <button className="btn btn-info btn-sm" type="button" disabled={busy} onClick={() => void save()}>
            Save
          </button>
        ) : null}

        <h5 style={{ marginTop: 24 }}>Tokens</h5>
        {fresh ? (
          <div className="alert alert-warning" role="alert" data-testid="scimFreshToken">
            <strong>Copy this token now - it is shown only once.</strong>
            <div className="input-group" style={{ marginTop: 6 }}>
              <input type="text" className="form-control" value={fresh} readOnly aria-label="New token" />
              <span className="input-group-btn">
                <button className="btn btn-default" type="button" onClick={() => copy(fresh, 'Token')}>
                  Copy
                </button>
              </span>
            </div>
            <button type="button" className="btn btn-link btn-xs" style={{ paddingLeft: 0 }} onClick={() => setFresh(null)}>
              I have copied it
            </button>
          </div>
        ) : null}
        {page.tokens.length === 0 ? (
          <div className="text-muted" style={{ padding: '8px 0' }}>
            No token yet.
          </div>
        ) : (
          <table className="table table-condensed" style={{ marginBottom: 8 }}>
            <thead>
              <tr>
                <th>Name</th>
                <th>Token</th>
                <th>Created</th>
                <th>Expires</th>
                <th>Last used</th>
                <th>State</th>
                {page.canWrite ? <th></th> : null}
              </tr>
            </thead>
            <tbody>
              {page.tokens.map((t) => (
                <tr key={t.id}>
                  <td>{t.name}</td>
                  <td className="text-muted" style={{ fontSize: 12 }}>
                    <code>{t.prefix}…</code>
                  </td>
                  <td className="text-muted" style={{ fontSize: 12 }}>
                    {t.createdAt}
                  </td>
                  <td className="text-muted" style={{ fontSize: 12 }}>
                    {t.expiresAt || 'never'}
                  </td>
                  <td className="text-muted" style={{ fontSize: 12 }}>
                    {t.lastUsedAt ? `${t.lastUsedAt} from ${t.lastUsedIp}` : '—'}
                  </td>
                  <td>
                    <span
                      className={`label label-${t.state === 'active' ? 'success' : 'default'}`}
                      style={{ fontWeight: 'normal' }}
                    >
                      {t.state}
                    </span>
                  </td>
                  {page.canWrite ? (
                    <td className="text-right">
                      {t.state === 'active' ? (
                        <button
                          type="button"
                          className="btn btn-link btn-xs text-danger"
                          onClick={() => void revoke(t.id, t.name)}
                        >
                          revoke
                        </button>
                      ) : null}
                    </td>
                  ) : null}
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {page.canWrite ? (
          <div className="form-inline" style={{ marginBottom: 12 }}>
            <input
              className="form-control input-sm"
              placeholder="Token name, e.g. OD IAM"
              aria-label="Token name"
              value={tokenName}
              maxLength={100}
              onChange={(e) => setTokenName(e.target.value)}
            />{' '}
            <input
              className="form-control input-sm"
              type="date"
              aria-label="Expires on"
              title="Optional expiry date"
              value={tokenExpiry}
              onChange={(e) => setTokenExpiry(e.target.value)}
            />{' '}
            <button
              className="btn btn-default btn-sm"
              type="button"
              disabled={busy || tokenName.trim() === ''}
              onClick={() => void generate()}
            >
              <i className="fas fa-key" aria-hidden="true"></i> Generate token
            </button>
          </div>
        ) : null}

        <h5 style={{ marginTop: 24 }}>Groups to roles</h5>
        <p className="text-muted" style={{ fontSize: 12 }}>
          The groups the IAM sends on a user (<code>groups</code>, <code>roles</code> or the OptimumDesk extension) are
          matched against the group rules of the provider - the same rules used at sign-in and by directory sync. The first
          matching rule gives the role; companies and extra roles follow the provider&apos;s IAM settings.
          {linked ? (
            <>
              {' '}
              <a href={`/app/settings/authentication/groups/${linked.id}`}>Edit the group rules of {linked.name}</a>
            </>
          ) : null}
        </p>
        {page.rules.length === 0 ? (
          <div className="text-muted" style={{ padding: '4px 0 8px' }}>
            {linked ? 'This provider has no group rules: everybody gets its default role.' : 'Choose a provider first.'}
          </div>
        ) : (
          <table className="table table-condensed" style={{ marginBottom: 8 }}>
            <thead>
              <tr>
                <th>Group</th>
                <th>Role</th>
                <th>Companies</th>
                <th>Active</th>
              </tr>
            </thead>
            <tbody>
              {page.rules.map((r) => (
                <tr key={r.id}>
                  <td>{r.group}</td>
                  <td className="text-muted">{r.role || '—'}</td>
                  <td className="text-muted" style={{ fontSize: 12 }}>
                    {r.allCompanies ? 'all companies' : r.companyIds.map(companyName).join(', ') || '—'}
                  </td>
                  <td>{r.active ? 'yes' : 'no'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        <h5 style={{ marginTop: 24 }}>Recent SCIM calls</h5>
        {page.calls.length === 0 ? (
          <div className="text-muted" style={{ padding: '8px 0' }}>
            Nothing recorded yet.
          </div>
        ) : (
          <table className="table table-condensed" style={{ marginBottom: 0 }}>
            <thead>
              <tr>
                <th>When</th>
                <th>Token</th>
                <th>Operation</th>
                <th>User</th>
                <th>Result</th>
                <th>Detail</th>
                <th>From</th>
              </tr>
            </thead>
            <tbody>
              {page.calls.map((c, i) => (
                <tr key={`${c.id}-${i}`}>
                  <td className="text-muted" style={{ fontSize: 12 }}>
                    {c.when}
                  </td>
                  <td className="text-muted" style={{ fontSize: 12 }}>
                    {c.token || '—'}
                  </td>
                  <td style={{ fontSize: 12 }}>
                    {c.method} {c.operation}
                  </td>
                  <td className="text-muted" style={{ fontSize: 12 }}>
                    {c.target || '—'}
                  </td>
                  <td>
                    <span className={`label label-${statusKind(c.status)}`} style={{ fontWeight: 'normal' }}>
                      {c.status}
                    </span>
                  </td>
                  <td className="text-muted" style={{ fontSize: 12 }}>
                    {c.detail}
                  </td>
                  <td className="text-muted" style={{ fontSize: 12 }}>
                    {c.from}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  )
}
